import { useQueryClient } from "@tanstack/react-query";
import { useApp } from "../appContext.js";
import { resetAllStores } from "../stores.js";
import { useActiveBand } from "../activeBand.js";
import { useDevImpersonation } from "../devImpersonation.js";
import { unregisterThisDevice } from "../pushRegistration.js";
import {
  login as loginRequest,
  verifyOtp as verifyOtpRequest,
  logout as logoutRequest,
  forgotPassword as forgotRequest,
  resetPassword as resetRequest,
  loginWithPasskey as loginWithPasskeyRequest,
  enrolPasskey as enrolPasskeyRequest,
  hasPasskey as hasPasskeyRequest,
  verifyEmail as verifyEmailRequest,
  joinByInvite as joinByInviteRequest,
} from "./session.js";

/**
 * The app-layer auth actions. Screens (feature code) call THESE — they never touch the API client
 * or TanStack Query directly (the no-raw-useQuery lint rule). Each action keeps the query cache honest:
 * login primes /me; logout scorches everything (cache + stores).
 */
export function useAuth() {
  const { api, tokenProvider } = useApp();
  const queryClient = useQueryClient();

  return {
    async login(input: { email: string; password: string }) {
      // Belt-and-braces on a shared web tab: a leftover active-band from a prior principal must not seed
      // the new session (the store deliberately opts out of the scorch registry — activeBand.ts).
      useActiveBand.getState().clear();
      const result = await loginRequest(api, tokenProvider, input);
      // Only a COMPLETED session touches the cache — an OTP challenge holds no principal yet.
      if (result.kind === "session") await queryClient.invalidateQueries({ queryKey: ["me"] });
      return result;
    },
    /** Complete the emailed-OTP second step; primes the cache exactly like a completed login. */
    async verifyOtp(input: { loginToken: string; code: string; trustDevice?: boolean; email?: string }) {
      const session = await verifyOtpRequest(api, tokenProvider, input);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      return session;
    },
    /**
     * Sign in with a platform passkey. Mirrors `login`'s cache handling exactly — clear any leftover
     * active band, then prime `me` — because a passkey mints a COMPLETED session with no OTP step.
     */
    async loginWithPasskey(input: { email?: string } = {}) {
      useActiveBand.getState().clear();
      const session = await loginWithPasskeyRequest(api, tokenProvider, input);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      return session;
    },
    /**
     * Create an account from an invite link and accept it (ART-239). Mirrors `login`'s cache handling: a NEW
     * principal, so any leftover active band is cleared and `me` is primed — the router then picks the shell.
     */
    async joinByInvite(input: { token: string; firstName: string; lastName: string; password: string }) {
      useActiveBand.getState().clear();
      const session = await joinByInviteRequest(api, tokenProvider, input);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      return session;
    },
    /** Is there a live session on this device? The /join page branches on it (accept vs create/sign in). */
    isSignedIn() {
      return tokenProvider.isAuthenticated();
    },
    /** Confirm an email address from its single-use link. Public — works signed out, which is the norm. */
    async verifyEmail(token: string) {
      await verifyEmailRequest(api, token);
    },
    /** Does the signed-in caller already hold a passkey? Gates the post-login enrolment offer. */
    async hasPasskey() {
      return await hasPasskeyRequest(api);
    },
    /** Enrol a passkey for the signed-in caller. No cache effect — it grants no new principal. */
    async enrolPasskey() {
      await enrolPasskeyRequest(api);
    },
    async logout() {
      // First, while the bearer still names the person leaving: stop pushing their notifications to this phone.
      await unregisterThisDevice(api);
      await logoutRequest(api, tokenProvider);
      queryClient.clear();
      resetAllStores();
      // The active-band store is excluded from resetAllStores() (switchBand re-sets it after the scorch),
      // so logout must clear it explicitly — else the next login on the same tab inherits the prior
      // principal's tenant context (a shared-device leak; the store's own clear() had no caller before).
      useActiveBand.getState().clear();
      // Same exclusion, same reason: the DEV seat swap survives the scorch (it IS the identity, not stale
      // tenant data), so logging out is the one path that must end it explicitly. Otherwise the swapped
      // marker would outlive the session it describes and greet the next login on this tab.
      useDevImpersonation.getState().end();
    },
    forgotPassword(email: string) {
      return forgotRequest(api, email);
    },
    resetPassword(input: { token: string; newPassword: string }) {
      return resetRequest(api, input);
    },
  };
}
